import { useState } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useRates } from './hooks/useRates';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus, Edit2, Trash2, Tag } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { RateModal } from './components/RateModal';
import type { ResourceRate } from './types/rates.types';
import { format } from 'date-fns';

export function EquipmentRatesPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.rates.view');

  const { data, isLoading, deleteRate, isDeleting } = useRates();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ResourceRate | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<ResourceRate | null>(null);

  const handleEdit = (item: ResourceRate) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteRate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <PageLayout title="Bảng giá tài nguyên" description="Quản lý sổ giá vật tư, thiết bị, phân thuốc theo nhà cung cấp">
      <div className="flex flex-col h-full gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Sổ giá (Rate Books)</h3>
          <AuthButton
            module="inventory.rates"
            action="create"
            behavior="disable"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 rounded-md inline-flex items-center gap-2 sm:text-sm text-xs"
            onClick={handleAdd}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Thêm đơn giá</span>
          </AuthButton>
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-md overflow-hidden min-h-0 flex flex-col">
          <div className="flex-1 overflow-auto min-h-0 relative">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Tài nguyên</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Nhà cung cấp</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32 text-right">Đơn giá (VNĐ)</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">VAT / Hao hụt</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Hiệu lực từ</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500 animate-pulse">Đang tải dữ liệu...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-12 text-center text-slate-500">
                      <Tag className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      Chưa có bảng giá nào được thiết lập.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.resource?.name || 'Tài nguyên đã xóa'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{item.resource?.code || ''}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="sm:text-sm text-xs text-slate-700">{item.supplier?.name || '-'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{item.supplier?.code || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-mono font-medium text-emerald-600 sm:text-sm text-xs">
                          {Number(item.price).toLocaleString()}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">/ {item.resource?.unit || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="sm:text-sm text-xs text-slate-700">
                          {item.vat_percent ? `${item.vat_percent}%` : '0%'}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          HH: {item.waste_percent ? `${item.waste_percent}%` : '0%'}
                        </div>
                      </td>
                      <td className="px-4 py-3 sm:text-sm text-xs text-slate-600">
                        {item.effective_from ? format(new Date(item.effective_from), 'dd/MM/yyyy') : '-'}
                        {item.status !== 'active' && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-700">
                            Ngừng
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <AuthButton
                          module="inventory.rates"
                          action="update"
                          behavior="hide"
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </AuthButton>
                        <AuthButton
                          module="inventory.rates"
                          action="delete"
                          behavior="hide"
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </AuthButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <RateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        rate={editingItem}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa đơn giá"
        message={
          <span>
            Bạn có chắc chắn muốn xóa đơn giá này?
          </span>
        }
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}

export default EquipmentRatesPage;
